<template>
  <v-container fluid class="holdings-details">
    <h1 class="text-h5 mb-4">Carteira diária</h1>

    <v-container fluid class="px-0">
      <v-row>
        <v-col cols="12" sm="6">
          <house-funds-selector
            label="Fundo"
            dense

            include-all-funds
            item-text="shortName"
            item-value="_id"
            :value="houseFundId"
            @change="(val) => changeHouseFundId(val)"
          />
        </v-col>

        <v-col cols="12" sm="6">
          <date-picker-field
            label="Data"
            dense

            v-model="refDate"
            :picker-props="{ allowedDates: getAllowedDates }"

            prepend-icon="mdi-arrow-left"
            @click:prepend="() => prevDate()"

            append-outer-icon="mdi-arrow-right"
            @click:append-outer="() => nextDate()"
          />
        </v-col>
      </v-row>
    </v-container>

    <template v-if="hasFoundData">
      <v-container fluid class="px-0">
        <v-row>
          <v-col cols="12">
            <holding-general-info :details="details" :house-fund-data="houseFundData" />
          </v-col>

          <v-col cols="12" v-if="details.fundsHoldings.length">
            <holding-funds :items="details.fundsHoldings" :meta="{ isLoyallFund, refDate }" />
          </v-col>

          <v-col cols="12" v-if="details.bondsHoldings.length">
            <holding-bonds :items="details.bondsHoldings" />
          </v-col>

          <!-- <v-col cols="12" v-if="details.futuresHoldings.length">
            <holding-futures :items="details.futuresHoldings" />
          </v-col> -->

          <v-col cols="12" v-if="details.futureOptionsHoldings.length">
            <holding-future-options :items="details.futureOptionsHoldings" />
          </v-col>

          <v-col cols="12" v-if="details.sharesHoldings.length">
            <holding-shares :items="details.sharesHoldings" />
          </v-col>

          <v-col cols="12" v-if="details.shareOptionsHoldings.length">
            <holding-share-options :items="details.shareOptionsHoldings" />
          </v-col>

          <v-col cols="12" v-if="details.loanHoldings.length">
            <holding-loans :items="details.loanHoldings" />
          </v-col>

          <v-col cols="12" v-if="details.repurchaseHoldings.length">
            <holding-repurchase :items="details.repurchaseHoldings" />
          </v-col>
        </v-row>
      </v-container>
    </template>

    <template v-else-if="hasRequestedData">
      <empty-state
        icon="mdi-calendar-remove"
        title="Posição não disponível"
        message="A posição de carteira para o dia em questão ainda não foi calculada."
      />
    </template>
  </v-container>
</template>

<script>
import moment from 'moment-loyall';

import { loyallFunds } from '@/config';

import api from '@/services/api';
import store from '@/services/store';
import router from '@/services/router';

import { loadValueFromUrl, saveValueOnUrl } from '@/utils/router-utils';

import EmptyState from '@/components/EmptyState.vue';
import DatePickerField from '@/components/DatePickerField.vue';
import HouseFundsSelector from '@/components/HouseFundsSelector.vue';

import HoldingGeneralInfo from '../components/HoldingGeneralInfo.vue';
import HoldingBonds from '../components/bonds/HoldingBonds.vue';
import HoldingLoans from '../components/loan/HoldingLoans.vue';
import HoldingFunds from '../components/funds/HoldingFunds.vue';
// import HoldingFutures from '../components/futures/HoldingFutures.vue';
import HoldingFutureOptions from '../components/futureoptions/HoldingFutureOptions.vue';
import HoldingRepurchase from '../components/repurchase/HoldingRepurchase.vue';
import HoldingShares from '../components/shares/HoldingShares.vue';
import HoldingShareOptions from '../components/shareoptions/HoldingShareOptions.vue';

const ALLOWED_STATUS = [
  'CALCULATED',
  'APPROVED',
  'APPROVED_WITH_WARNINGS',
];

const loyallFundIds = loyallFunds.map((fund) => fund.houseFundId);

export default {
  name: 'HoldingDetails',

  components: {
    EmptyState,
    DatePickerField,
    HouseFundsSelector,

    HoldingGeneralInfo,
    HoldingBonds,
    HoldingLoans,
    HoldingFunds,
    // HoldingFutures,
    HoldingFutureOptions,
    HoldingRepurchase,
    HoldingShares,
    HoldingShareOptions,
  },

  props: {
    houseFundId: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    refDate: loadValueFromUrl('refDate') ?? '',

    details: null,
    hasRequestedData: false,
  }),

  computed: {
    hasFoundData: (vm) => Object.keys(vm.details ?? {}).length > 0,

    isLoyallFund: (vm) => loyallFundIds.includes(vm.houseFundId),
    houseFundData: (vm) => store.state.houseFunds?.results.find(
      (item) => item._id === vm.houseFundId,
    ),
  },

  watch: {
    refDate(newValue) {
      saveValueOnUrl('refDate', newValue);

      this.details = null;
      this.hasRequestedData = false;
      this.fetchData();
    },

    houseFundId() {
      this.details = null;
      this.hasRequestedData = false;
      this.fetchData();
    },
  },

  created() {
    store.dispatch('houseFunds/fetchList');
    this.fetchData();
  },

  beforeRouteUpdate(to, from, next) {
    this.refDate = to.query.refDate;

    next();
  },

  methods: {
    getAllowedDates: (date) => moment(date).isBusinessDay('brasil'),

    prevDate() {
      const targetDate = moment(this.refDate).prevBusinessDay('brasil');
      this.refDate = targetDate.format('YYYY-MM-DD');
    },

    nextDate() {
      const targetDate = moment(this.refDate).nextBusinessDay('brasil');
      this.refDate = targetDate.format('YYYY-MM-DD');
    },

    changeHouseFundId(newId) {
      const { hash, query } = router.currentRoute;
      router.replace({ path: `/holdings/${newId}`, hash, query });
    },

    async fetchData() {
      store.dispatch('layout/setLoadingState', true);

      try {
        const { data } = await api.holdings.getHoldingDetails(this.houseFundId, {
          refDate: this.refDate,
          status: ALLOWED_STATUS.join(','),
        });

        this.details = data;

        if (data && !this.refDate) {
          this.refDate = data.refDate?.substring(0, 10);
        }
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          return;
        }

        store.dispatch('alert/showAlert', { errorList: [error.message] });
      } finally {
        this.hasRequestedData = true;
        store.dispatch('layout/setLoadingState', false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  th {
    white-space: nowrap;
  }
}
</style>
